import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSButton from "../../../Components/Button/WSButton";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";

interface ScanSubCategory {
  id: string;
  name: string;
  description: string;
  alsoKnownAs: string;
  preparationInstructions: string;
  turnaroundTime: string;
  imgUrl: string;
  scancategory?: { name: string };
}

export const ScanSubCategoryList: React.FC<any> = React.memo(() => {
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    updateMainHeader("Scan Sub Category");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Sub Category": "/scan-sub-category/list" },
    ]);
  }, []);

  const fetchSubCategories = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError("");
      const response: any = await http.post("pharmacy/ScanSubCategory/search", {});
      const list =
        response?.data || response?.items || response?.categories || response || [];
      const items: ScanSubCategory[] = Array.isArray(list) ? list : [];

      const transformed = items.map((item) => ({
        ...item,
        scanCategoryName: item.scancategory?.name || "N/A",
        alsoKnownAsDisplay: item.alsoKnownAs || "-",
        turnaroundTimeDisplay: item.turnaroundTime || "-",
      }));

      setData(transformed);
    } catch (err: any) {
      console.error("fetchSubCategories error:", err);
      setError(err?.message || "Failed to fetch scan sub categories");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!id || !window.confirm("Delete this scan sub category?")) return;
      try {
        await http.del(`pharmacy/ScanSubCategory/${id}`);
        fetchSubCategories();
      } catch (err: any) {
        console.error("Delete error:", err);
      }
    },
    [fetchSubCategories]
  );

  const handleAction = (action: string, row: any) => {
    if (action === "Edit") {
      navigate(`/scan-sub-category/${row?.id}`);
    } else if (action === "Delete") {
      handleDelete(row?.id);
    }
  };

  const handleAdd = () => navigate("/scan-sub-category/add");

  const columns: IColumns[] = [
    { header: "Name", fieldName: "name", type: "default" },
    { header: "Scan Category", fieldName: "scanCategoryName", type: "default" },
    { header: "Also Known As", fieldName: "alsoKnownAsDisplay", type: "default" },
    { header: "Turn Around Time", fieldName: "turnaroundTimeDisplay", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete"],
      actionCallback: (action, row) => handleAction(action, row),
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading scan sub categories...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={12} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddScanSubCategory"
            label="Add Scan Sub Category"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": { backgroundColor: "#087654!important" },
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          {error ? (
            <div style={{ padding: 20, color: "red" }}>
              {error}. <button onClick={fetchSubCategories}>Retry</button>
            </div>
          ) : (
            <WSDataGridNew
              columns={columns}
              data={data}
              id="scanSubCategoryListGrid"
              title={`Scan Sub Categories (${data.length})`}
              exportCsv={true}
              searchBox={{ fieldNames: ["name", "scanCategoryName"] }}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
});

export default ScanSubCategoryList;
